import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ChevronLeft,
  Users,
  Trophy,
  CheckCircle,
  SwitchCamera,
  LogIn,
} from 'lucide-react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

import GlassPanel from '../components/GlassPanel'
import Button from '../components/Button'
import { sessionApi } from '../services/sessionApi'
import {
  Session,
  SessionStatus,
  PowerupType,
  TeamPowerup,
  Difficulty
} from '../types/session'
import { Category } from '../types/category'
import { categoryApi } from '../services/categoryApi'
import { useTheme, themeStyles } from '../context/ThemeContext'
import ParticleBackground from '../components/ParticleBackground'

const GameSession: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { sessionId } = useParams<{ sessionId: string }>()
  const { theme } = useTheme()
  const currentTheme = themeStyles[theme]

  const [session, setSession] = useState<Session | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [currentTurn, setCurrentTurn] = useState<string>('')
  const [showConfirmEndModal, setShowConfirmEndModal] = useState<boolean>(false)

  // -------------------------------------------
  // Fetch session + categories on mount
  // -------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      if (!sessionId) return
      try {
        const data = await sessionApi.getSession(sessionId)

        // Convert object => Map
        const categoryQuestionsMap = new Map(
          Object.entries(data.categoryQuestionsMap)
        )
        const updatedSession: Session = {
          ...data,
          categoryQuestionsMap,
          // Ensure powerup arrays exist
          team1Powerups: data.team1Powerups || [],
          team2Powerups: data.team2Powerups || [],
        }
        setSession(updatedSession)

        // If no turn in location state, default to team1
        const defaultTurn =
          location?.state?.currentTurn ?? updatedSession.team1name
        setCurrentTurn(defaultTurn)

        // If categories exist, fetch them
        if (
          updatedSession.gameCategories &&
          updatedSession.gameCategories.length > 0
        ) {
          const cats = await categoryApi.getListCategory(
            updatedSession.gameCategories
          )
          setCategories(cats)
        }
      } catch (err) {
        console.error('Error fetching session/categories:', err)
      }
    }
    fetchData()
  }, [sessionId, location])

  // -------------------------------------------
  // Update scores from location if present
  // (but do NOT override currentTurn)
  // -------------------------------------------
  useEffect(() => {
    if (!session) return
    const locState = location.state as any
    if (locState && (locState.team1Score !== undefined || locState.team2Score !== undefined)) {
      const newTeam1Score = locState.team1Score ?? session.team1score
      const newTeam2Score = locState.team2Score ?? session.team2score
      
      // Only update if scores actually changed
      if (newTeam1Score !== session.team1score || newTeam2Score !== session.team2score) {
        setSession(prev => {
          if (!prev) return prev
          return {
            ...prev,
            team1score: newTeam1Score,
            team2score: newTeam2Score,
          }
        })
      }
    }
  }, [location.state, session]) // Include session in dependencies

  // -------------------------------------------
  // If session completed => final score
  // -------------------------------------------
  useEffect(() => {
    if (!session) return
    if (session.sessionStatus === SessionStatus.COMPLETED) {
      navigate(`/final-score/${sessionId}`, {
        state: {
          team1Name: session.team1name,
          team2Name: session.team2name,
          team1Score: session.team1score,
          team2Score: session.team2score,
          team1Powerups: session.team1Powerups,
          team2Powerups: session.team2Powerups,
        },
      })
    }
  }, [session, navigate, sessionId])

  // -------------------------------------------
  // Power-ups logic
  // -------------------------------------------
  const hasActivePowerup = (powerups: TeamPowerup[]) => {
    return powerups.some((p) => p.active && !p.used)
  }

  const handlePowerupToggle = (teamName: string, index: number) => {
    if (!session) return
    const isCurrentTeam = currentTurn === teamName
    const powerupsKey =
      teamName === session.team1name ? 'team1Powerups' : 'team2Powerups'
    const oldPowerups = session[powerupsKey]
    const powerup = oldPowerups[index]

    // Determine if toggling is allowed
    const canToggle =
      (powerup.type === PowerupType.DOUBLE_OR_MINUS && isCurrentTeam) ||
      (powerup.type === PowerupType.NO_ANSWER && isCurrentTeam) ||
      (powerup.type === PowerupType.MINUS_FIFTY_PERCENT && !isCurrentTeam)

    if (!canToggle || powerup.used) return
    // Only one active powerup at a time
    if (!powerup.active && hasActivePowerup(oldPowerups)) return

    const updatedPowerups = oldPowerups.map((p, i) =>
      i === index ? { ...p, active: !p.active } : p
    )

    setSession({
      ...session,
      [powerupsKey]: updatedPowerups,
    })
  }

  // -------------------------------------------
  // Navigate to question page
  // -------------------------------------------
  const handleQuestionClick = (
    categoryId: string,
    questionId: string,
    points: number
  ) => {
    if (!session) return
    // If question is answered, do nothing
    const catQuestions = session.categoryQuestionsMap.get(categoryId) || []
    const question = catQuestions.find((q) => q.questionId === questionId)
    if (question?.answered) return

    navigate(`/question/${questionId}`, {
      state: {
        sessionId,
        points,
        currentTurn,
        team1Score: session.team1score,
        team2Score: session.team2score,
        team1Name: session.team1name,
        team2Name: session.team2name,
        team1Powerups: session.team1Powerups,
        team2Powerups: session.team2Powerups,
      },
    })
  }

  // -------------------------------------------
  // End session => final score
  // -------------------------------------------
  const handleEndSession = async () => {
    if (!session) return
    session.sessionStatus = SessionStatus.COMPLETED
    await sessionApi.updateSession(session)
    navigate(`/final-score/${sessionId}`, {
      state: {
        team1Name: session.team1name,
        team2Name: session.team2name,
        team1Score: session.team1score,
        team2Score: session.team2score,
      },
    })
  }

  // Leave session without ending it
  const handleLeaveSession = () => {
    navigate('/')
  }

  // -------------------------------------------
  // Switch turn
  // -------------------------------------------
  const handleSwitchTurn = () => {
    if (!session) return
    const nextTurn =
      currentTurn === session.team1name ? session.team2name : session.team1name
    setCurrentTurn(nextTurn)
  }

  // -------------------------------------------
  // If still loading or no categories
  // -------------------------------------------
  if (!session || categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme === 'dark' ? 'border-white' : 'border-gray-600'}`} />
      </div>
    )
  }

  // ------------------------------------------------------
  // RENDER
  // ------------------------------------------------------
  return (
    <div className={`relative h-screen w-screen overflow-hidden bg-gradient-to-br ${currentTheme.background}`}>
      {/* Background particles */}
      <ParticleBackground />

      {/* Exit Button - Top Left */}
      <div className="fixed top-[3vh] left-[3vh] z-50">
        <Button
          variant="outline"
          onClick={handleLeaveSession}
          className="rounded-full inline-flex items-center gap-[0.5vw] py-[1vh] px-[1vw] shadow-lg"
          icon={<LogIn className="w-[2vh] h-[2vh] transform scale-x-[-1]" />}
        >
          <span className="text-[1.7vh]">خروج</span>
        </Button>
      </div>

      {/* End Session Button - Top Right */}
      <div className="fixed top-[3vh] right-[3vh] z-50">
        <Button
          variant="gradient"
          onClick={() => setShowConfirmEndModal(true)}
          className="rounded-full inline-flex items-center gap-[0.5vw] py-[1vh] px-[1vw] shadow-lg"
          icon={<ChevronLeft className="w-[2vh] h-[2vh]" />}
        >
          <span className="text-[1.7vh]">إنهاء الجلسة</span>
        </Button>
      </div>

      {/* Confirmation Modal for Ending Session */}
      {showConfirmEndModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <GlassPanel className="w-[40vw] p-[2vh] rounded-[1vh] shadow-lg">
            <div className="flex flex-col items-center">
              <h3 className="text-[2.5vh] font-bold mb-[2vh]">تأكيد إنهاء الجلسة</h3>
              <p className="text-[1.8vh] mb-[3vh] text-center">هل أنت متأكد من إنهاء الجلسة؟ سيتم تسجيل النتائج النهائية.</p>
              <div className="flex gap-[1vw]">
                <Button
                  variant="gradient"
                  onClick={handleEndSession}
                  className="rounded-full py-[1vh] px-[2vw]"
                >
                  <span className="text-[1.7vh]">نعم، إنهاء الجلسة</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmEndModal(false)}
                  className="rounded-full py-[1vh] px-[2vw]"
                >
                  <span className="text-[1.7vh]">إلغاء</span>
                </Button>
              </div>
            </div>
          </GlassPanel>
        </div>
      )}

      {/* Main Container - 90% width with small bottom margin */}
      <div className="w-[90vw] h-[88vh] absolute top-[48%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        {/* SCOREBOARD - reduced to 14% of container height */}
        <div className="h-[15%] w-full mb-[1%]">
          <GlassPanel className="w-full h-full p-[1.2vh] rounded-[1vh] shadow-md">
            <div className="flex flex-row items-center justify-around w-full h-full">
              {/* TEAM 1 */}
              <div className="flex flex-col items-center justify-center h-full w-[30%]">
                <h2
                  className={`font-bold text-center w-full ${
                    currentTurn === session.team1name
                      ? 'text-[3vh] text-[#4B7BFF]'
                      : `text-[2.5vh] ${currentTheme.text}`
                  }`}
                >
                  {session.team1name}
                </h2>
                <div className="flex items-center gap-[0.8vw] my-[0.7vh]">
                  <Trophy className="text-[#FDCB6E] w-[2.5vh] h-[2.5vh]" />
                  <span
                    className={
                      currentTurn === session.team1name
                        ? 'text-[2.5vh] font-bold'
                        : 'text-[2.2vh] font-bold'
                    }
                  >
                    {session.team1score}
                  </span>
                </div>
                {/* Team1 Powerups */}
                <div className="flex flex-wrap gap-[0.6vh] items-center justify-center">
                  {session.team1Powerups.map((p, i) => {
                    const canUse =
                      (p.type === PowerupType.DOUBLE_OR_MINUS &&
                        currentTurn === session.team1name) ||
                      (p.type === PowerupType.NO_ANSWER &&
                        currentTurn === session.team1name) ||
                      (p.type === PowerupType.MINUS_FIFTY_PERCENT &&
                        currentTurn !== session.team1name)

                    const disabled =
                      p.used ||
                      (!p.active && canUse && hasActivePowerup(session.team1Powerups))

                    return (
                      <button
                        key={i}
                        onClick={() => handlePowerupToggle(session.team1name, i)}
                        disabled={disabled}
                        className={`text-[1.2vh] px-[0.7vw] py-[0.4vh] rounded-[0.4vh] font-semibold 
                          ${
                            p.used
                              ? 'bg-gray-700/50 text-gray-400 line-through cursor-not-allowed'
                              : p.active
                              ? 'bg-[#9B59B6] text-white shadow-md'
                              : canUse
                              ? 'bg-[#4B7BFF] text-white hover:bg-[#2F5ED9] shadow-md'
                              : 'bg-gray-500/50 text-gray-300 cursor-not-allowed'
                          }
                          transition-all duration-300
                        `}
                        title={p.type}
                      >
                        {p.type === PowerupType.MINUS_FIFTY_PERCENT && 'نصف النقاط'}
                        {p.type === PowerupType.NO_ANSWER && 'منع الإجابة'}
                        {p.type === PowerupType.DOUBLE_OR_MINUS && 'ضعف أو خصم'}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* TURN SWITCH */}
              <motion.div
                key={currentTurn}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex flex-col items-center justify-center w-[20%]"
              >
                <div className="flex items-center gap-[1vh] mb-[0.8vh]">
                  <Users className="text-[#6C5CE7] w-[3vh] h-[3vh]" />
                  <span className="cursor-pointer" onClick={handleSwitchTurn}>
                    <SwitchCamera className="text-[#6C5CE7] w-[3vh] h-[3vh]" />
                  </span>
                </div>
                <span className="text-[2vh] font-semibold bg-gradient-to-r from-[#FF7675] to-[#FDCB6E] bg-clip-text text-transparent">
                  دور {currentTurn.toUpperCase()}
                </span>
              </motion.div>

              {/* TEAM 2 */}
              <div className="flex flex-col items-center justify-center h-full w-[30%]">
                <h2
                  className={`font-bold text-center w-full ${
                    currentTurn === session.team2name
                      ? 'text-[3vh] text-[#4B7BFF]'
                      : `text-[2.5vh] ${currentTheme.text}`
                  }`}
                >
                  {session.team2name}
                </h2>
                <div className="flex items-center gap-[0.8vw] my-[0.7vh]">
                  <Trophy className="text-[#FDCB6E] w-[2.5vh] h-[2.5vh]" />
                  <span
                    className={
                      currentTurn === session.team2name
                        ? 'text-[2.5vh] font-bold'
                        : 'text-[2.2vh] font-bold'
                    }
                  >
                    {session.team2score}
                  </span>
                </div>
                {/* Team2 Powerups */}
                <div className="flex flex-wrap gap-[0.6vh] items-center justify-center">
                  {session.team2Powerups.map((p, i) => {
                    const canUse =
                      (p.type === PowerupType.DOUBLE_OR_MINUS &&
                        currentTurn === session.team2name) ||
                      (p.type === PowerupType.NO_ANSWER &&
                        currentTurn === session.team2name) ||
                      (p.type === PowerupType.MINUS_FIFTY_PERCENT &&
                        currentTurn !== session.team2name)

                    const disabled =
                      p.used ||
                      (!p.active && canUse && hasActivePowerup(session.team2Powerups))

                    return (
                      <button
                        key={i}
                        onClick={() => handlePowerupToggle(session.team2name, i)}
                        disabled={disabled}
                        className={`text-[1.2vh] px-[0.7vw] py-[0.4vh] rounded-[0.4vh] font-semibold 
                          ${
                            p.used
                              ? 'bg-gray-700/50 text-gray-400 line-through cursor-not-allowed'
                              : p.active
                              ? 'bg-[#9B59B6] text-white shadow-md'
                              : canUse
                              ? 'bg-[#4B7BFF] text-white hover:bg-[#2F5ED9] shadow-md'
                              : 'bg-gray-500/50 text-gray-300 cursor-not-allowed'
                          }
                          transition-all duration-300
                        `}
                        title={p.type}
                      >
                        {p.type === PowerupType.MINUS_FIFTY_PERCENT && 'نصف النقاط'}
                        {p.type === PowerupType.NO_ANSWER && 'منع الإجابة'}
                        {p.type === PowerupType.DOUBLE_OR_MINUS && 'ضعف أو خصم'}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </GlassPanel>
        </div>

        {/* CATEGORIES (3 columns x 2 rows = 6 total) - equal sizing */}
        <div className="h-[85%] w-full">
          <div className="grid grid-cols-3 grid-rows-2 gap-[1.5vh] h-full">
            {categories.slice(0, 6).map((cat, i) => {
              const catId = cat.categoryId || String(i)
              const questions = session.categoryQuestionsMap.get(catId) || []
              // Ensure we have 8 questions (add placeholders if needed)
              const filledQuestions = [...questions]
              while (filledQuestions.length < 8) {
                filledQuestions.push({
                  questionId: `placeholder-${filledQuestions.length}`,
                  categoryId: catId,
                  pointsAwarded: 0,
                  difficulty: Difficulty.EASY,
                  answered: false,
                  answeredByTeam: 'NO ONE',
                  answeredCorrectly: false
                })
              }

              return (
                <GlassPanel
                  key={catId}
                  className="p-[1.5vh] rounded-[1vh] shadow-md flex flex-col h-full w-full"
                >
                  {/* Category Panel */}
                  <div className="flex flex-col h-full w-full">
                    {/* Category Header with overlapping image and title */}
                    <div className="relative mb-[2vh] h-[25%] flex items-center justify-center">
                      {cat.thumbnailUrl && (
                        <img
                          src={cat.thumbnailUrl}
                          alt={cat.categoryName}
                          className="w-[10vh] h-[10vh] object-contain rounded-[1vh] drop-shadow-lg"
                        />
                      )}
                      <div className="absolute bottom-[-1vh] left-0 right-0 flex items-center justify-center">
                        <h3 className={`font-bold text-center ${theme === 'dark' ? 'text-[#4B7BFF]' : 'text-[#3B5ECC]'} text-[2.3vh] px-[1vh] py-[0.3vh] ${theme === 'dark' ? 'bg-gray-900/80' : 'bg-white/80'} backdrop-blur-sm rounded-[0.8vh] shadow-md z-10`}>
                          {cat.categoryName || `فئة ${i + 1}`}
                        </h3>
                      </div>
                    </div>
                  
                    {/* Question Grid - 2 questions per row, 4 rows total = 8 questions */}
                    <div className="h-[75%] grid grid-cols-2 grid-rows-4 gap-[0.8vh]">
                      {filledQuestions.map((question, qIndex) => {
                        // Skip placeholders
                        if (question.questionId.startsWith('placeholder-')) {
                          return (
                            <div 
                              key={`placeholder-${qIndex}`}
                              className={`relative rounded-[1vh] flex items-center justify-center ${theme === 'dark' ? 'bg-gray-800/10' : 'bg-gray-200/30'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-300/30'}`}
                            />
                          )
                        }
                        
                        return (
                          <motion.div
                            key={question.questionId || qIndex}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: qIndex * 0.03 }}
                            whileHover={
                              !question.answered ? { scale: 1.05 } : undefined
                            }
                            className={`relative rounded-[1vh] flex items-center justify-center border ${theme === 'dark' ? 'border-white/20' : 'border-gray-300/50'}
                              ${
                                question.answered
                                  ? `${theme === 'dark' ? 'bg-gray-700/40' : 'bg-gray-300/40'} cursor-not-allowed`
                                  : `${theme === 'dark' ? 'bg-gray-800/20 hover:bg-gray-700/30' : 'bg-gray-200/50 hover:bg-gray-300/70'} cursor-pointer`
                              }
                              transition-all overflow-hidden h-full w-full`}
                            onClick={() =>
                              !question.answered &&
                              handleQuestionClick(
                                catId,
                                question.questionId,
                                question.pointsAwarded
                              )
                            }
                          >
                            {/* Difficulty overlay */}
                            <div
                              className={`absolute inset-0 pointer-events-none opacity-20
                                ${
                                  question.difficulty === 'HARD'
                                    ? 'bg-red-400'
                                    : question.difficulty === 'MEDIUM'
                                    ? 'bg-yellow-400'
                                    : 'bg-cyan-400'
                                }
                              `}
                            />
                            <span
                              className={`z-10 font-bold text-[2.6vh] ${
                                question.answered ? 'opacity-40' : 'opacity-100'
                              }`}
                              style={{
                                color: question.answered
                                  ? theme === 'dark' ? '#ffffff' : '#555555'
                                  : question.difficulty === 'HARD'
                                  ? '#FF7675'
                                  : question.difficulty === 'MEDIUM'
                                  ? '#FDCB6E'
                                  : '#00CEC9',
                              }}
                            >
                              {question.pointsAwarded}
                            </span>
                            {question.answered && (
                              <div className={`absolute bottom-[0.5vh] left-[0.5vh] right-[0.5vh] flex items-center justify-between text-[1.6vh] ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} z-20`}>
                                {question.answeredByTeam !== 'NO ONE' ? (
                                  <span
                                    className="truncate max-w-[70%] text-left"
                                    title={question.answeredByTeam}
                                  >
                                    {question.answeredByTeam}
                                  </span>
                                ) : (
                                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>لا أحد</span>
                                )}
                                <CheckCircle className="text-green-400 w-[2vh] h-[2vh]" />
                              </div>
                            )}
                          </motion.div>
                        )
                      })}
                    </div>
                  </div>
                </GlassPanel>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GameSession
